import { FC, useEffect, useRef } from 'react';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DraggableProvided,
  DraggableRubric,
  DraggableStateSnapshot,
  DropResult,
} from '@hello-pangea/dnd';
import { Plus, Trash2, AlertCircle, Check, GripVertical } from 'lucide-react';
import { Button } from '@new/common/ui/Button/Button';
import { Input } from '@new/common/ui/Input/Input';
import { MultiSelect } from '@new/common/ui/MultiSelect/MultiSelect';
import { SectionHeading } from '@new/common/ui/SectionHeading/SectionHeading';
import { Tooltip } from '@new/common/ui/Tooltip/Tooltip';
import type { CourseCategory } from '@new/features/transcripts/shared/types';
import {
  emptyEditableRow,
  formatOfficialSummary,
  removeRow,
  reorderRows,
  resolveRow,
  type EditableCourseRow,
} from './courseRows';

import './TranscriptCoursesEditor.css';

const categoryIdsToValues = (categoryIds: number[] | null | undefined): string[] =>
  (categoryIds ?? []).map((id) => String(id));

const valuesToCategoryIds = (values: string[]): number[] => values.map((v) => Number(v));

interface TranscriptCoursesEditorProps {
  rows: EditableCourseRow[];
  categories: CourseCategory[];
  onRowsChange: (updater: (rows: EditableCourseRow[]) => EditableCourseRow[]) => void;
  onReorder?: (courseIds: number[]) => void;
}

interface CourseRowCardProps {
  row: EditableCourseRow;
  categoryOptions: { label: string; value: string }[];
  isDragging: boolean;
  onUpdateRow: (key: string, patch: Partial<EditableCourseRow>) => void;
  onRemoveRow: (key: string) => void;
  onResolveRow: (key: string) => void;
}

const INPUT_OVERRIDES =
  '[&_input[data-slot=input]]:h-12! [&_input[data-slot=input]]:md:h-12! [&_input[data-slot=input]]:xl:h-12! [&_input[data-slot=input]]:rounded-xl! [&_input[data-slot=input]]:border-[1.5px]! [&_input[data-slot=input]]:border-solid! [&_input[data-slot=input]]:border-(--neutral-white-NW800)! [&_input[data-slot=input]]:bg-(--neutral-white-NW900)!';

const hasInconsistency = (row: EditableCourseRow): boolean =>
  row.validationStatus != null && row.notes != null && row.notes.trim().length > 0 && !row.resolved;

const CourseRowCard: FC<CourseRowCardProps> = ({
  row,
  categoryOptions,
  isDragging,
  onUpdateRow,
  onRemoveRow,
  onResolveRow,
}) => {
  const inconsistent = hasInconsistency(row);

  return (
    <div
      className={`flex flex-col gap-3 rounded-lg border p-3 transition-colors ${
        inconsistent
          ? 'bg-(--danger-D200)/70 border-(--danger-D400)'
          : 'bg-(--neutral-white-NW1000) border-(--neutral-black-NB100)'
      } ${isDragging ? 'border-(--primary-O900) shadow-md' : ''}`}
    >
      <div className="flex gap-2">
        <div className="flex items-center justify-center text-(--neutral-black-NB400)">
          <GripVertical className="size-4" />
        </div>
        <div className="flex flex-3 flex-col items-start gap-2">
          <label className="[font-size:var(--fs-p4)]! font-medium text-(--neutral-black-NB1000)!">
            Course name
          </label>
          <Input
            placeholder="Course name"
            value={row.rawName}
            onChange={(v) => onUpdateRow(row.key, { rawName: v })}
          />
        </div>

        <div className="flex flex-1 flex-col items-start gap-2">
          <label className="[font-size:var(--fs-p4)]! font-medium text-(--neutral-black-NB1000)!">
            Code
          </label>
          <Input
            placeholder="Code"
            value={row.rawCode ?? ''}
            onChange={(v) => onUpdateRow(row.key, { rawCode: v || null })}
          />
        </div>

        <div className="flex flex-1 flex-col items-start gap-2">
          <label className="[font-size:var(--fs-p4)]! font-medium text-(--neutral-black-NB1000)!">
            Grade
          </label>
          <Input
            placeholder="Grade"
            value={row.grade ?? ''}
            onChange={(v) => onUpdateRow(row.key, { grade: v || null })}
          />
        </div>

        <div className="flex flex-1 flex-col items-start gap-2">
          <label className="[font-size:var(--fs-p4)]! font-medium text-(--neutral-black-NB1000)!">
            Credit
          </label>
          <Input<number | string>
            type="number"
            min={0}
            step={1}
            placeholder="Credit"
            value={row.creditHours == null ? '' : Math.round(Number(row.creditHours))}
            onChange={(v) =>
              onUpdateRow(row.key, {
                creditHours: v === '' ? null : Math.round(Number(v)),
              })
            }
            onWheel={(e) => {
              e.currentTarget.blur();
            }}
          />
        </div>

        <div className="flex w-80 items-start">
          <MultiSelect
            value={categoryIdsToValues(row.categoryIds)}
            onChange={(v) => onUpdateRow(row.key, { categoryIds: valuesToCategoryIds(v) })}
            options={categoryOptions}
            label="Category"
            placeholder="Category"
            className="**:data-[slot=select]:h-12! **:data-[slot=select]:rounded-xl! **:data-[slot=select]:border-[1.5px]! **:data-[slot=select]:border-solid! **:data-[slot=select]:border-[#E1E7EA]! **:data-[slot=select]:bg-[#FCFDFD]!"
          />
        </div>

        <div className="flex flex-col items-center justify-center gap-1">
          {inconsistent ? (
            <button
              type="button"
              aria-label="Accept course changes"
              onClick={() => onResolveRow(row.key)}
              className="cursor-pointer p-1 bg-(--success-S700) rounded-full!"
            >
              <Check className="size-4 text-(--success-S100)" />
            </button>
          ) : null}
          <button
            type="button"
            aria-label={
              row.validationStatus === 'new' ? 'Dismiss proposed addition' : 'Remove course'
            }
            onClick={() => onRemoveRow(row.key)}
            className="cursor-pointer p-2 text-(--neutral-black-NB600) hover:text-(--danger-D700)"
          >
            <Trash2 className="size-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export const TranscriptCoursesEditor: FC<TranscriptCoursesEditorProps> = ({
  rows,
  categories,
  onRowsChange,
  onReorder,
}) => {
  const categoryOptions = categories.map((c) => ({ label: c.name, value: String(c.id) }));
  const coursesEndRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const prevCountRef = useRef(rows.length);
  useEffect(() => {
    if (rows.length > prevCountRef.current) {
      coursesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
    prevCountRef.current = rows.length;
  }, [rows.length]);

  const onUpdateRow = (key: string, patch: Partial<EditableCourseRow>) =>
    onRowsChange((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const onRemoveRow = (key: string) => onRowsChange((prev) => removeRow(prev, key));
  const onAddRow = () => onRowsChange((prev) => [...prev, emptyEditableRow()]);
  const onResolveRow = (key: string) => onRowsChange((prev) => resolveRow(prev, key));

  const onDragEnd = (result: DropResult) => {
    if (!result.destination || result.destination.index === result.source.index) {
      return;
    }
    const reordered = reorderRows(rows, result.source.index, result.destination.index);
    onRowsChange(() => reordered);
    onReorder?.(reordered.flatMap((r) => (r.id != null ? [r.id] : [])));
  };

  const renderClone = (
    provided: DraggableProvided,
    _snapshot: DraggableStateSnapshot,
    rubric: DraggableRubric
  ) => {
    const row = rows[rubric.source.index];
    return (
      <div
        ref={provided.innerRef}
        {...provided.draggableProps}
        {...provided.dragHandleProps}
        className={`transcript-courses-editor ${INPUT_OVERRIDES}`}
        style={{
          ...provided.draggableProps.style,
          width: listRef.current?.clientWidth,
        }}
      >
        <CourseRowCard
          row={row}
          categoryOptions={categoryOptions}
          isDragging
          onUpdateRow={onUpdateRow}
          onRemoveRow={onRemoveRow}
          onResolveRow={onResolveRow}
        />
      </div>
    );
  };

  return (
    <div className={`transcript-courses-editor flex flex-col gap-3 ${INPUT_OVERRIDES}`}>
      <SectionHeading title={`Courses${rows.length ? ` (${rows.length})` : ''}`} />
      {rows.length === 0 ? (
        <p className="[font-size:var(--fs-p4)]! text-(--neutral-black-NB700)!">No courses added.</p>
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="transcript-courses" renderClone={renderClone}>
            {(dropProvided) => (
              <div
                ref={(el) => {
                  dropProvided.innerRef(el);
                  listRef.current = el;
                }}
                {...dropProvided.droppableProps}
                className="flex flex-col gap-2 rounded-lg bg-(--neutral-white-NW1000)"
              >
                {rows.map((row, index) => {
                  const inconsistent = hasInconsistency(row);
                  const officialSummary = formatOfficialSummary(row);

                  return (
                    <Draggable key={row.key} draggableId={row.key} index={index}>
                      {(dragProvided, dragSnapshot) => (
                        <div
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          {...dragProvided.dragHandleProps}
                          className={`cursor-grab active:cursor-grabbing ${
                            dragSnapshot.isDragging ? 'opacity-50' : ''
                          }`}
                        >
                          {inconsistent ? (
                            <Tooltip
                              wrapperClassName="w-full"
                              content={
                                <div className="flex items-start gap-2">
                                  <AlertCircle className="size-4 shrink-0 text-(--danger-D700)" />
                                  <span className="text-(--danger-D700)! m-0!">
                                    {row.notes}
                                    {officialSummary ? ` ${officialSummary}` : null}
                                  </span>
                                </div>
                              }
                            >
                              <CourseRowCard
                                row={row}
                                categoryOptions={categoryOptions}
                                isDragging={false}
                                onUpdateRow={onUpdateRow}
                                onRemoveRow={onRemoveRow}
                                onResolveRow={onResolveRow}
                              />
                            </Tooltip>
                          ) : (
                            <CourseRowCard
                              row={row}
                              categoryOptions={categoryOptions}
                              isDragging={false}
                              onUpdateRow={onUpdateRow}
                              onRemoveRow={onRemoveRow}
                              onResolveRow={onResolveRow}
                            />
                          )}
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {dropProvided.placeholder}
                <div ref={coursesEndRef} />
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}
      <Button
        variant="ghost"
        size="sm"
        leftIcon={<Plus className="size-4" />}
        onClick={onAddRow}
        text="Add course"
        className="w-fit"
      />
    </div>
  );
};
